import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Loader2, ArrowLeft, UserPlus, CheckCircle2, Clock, Lock, Unlock, ShieldCheck, Trash2, X,
} from 'lucide-react'
import { apiGet, cachedGet, patchJson, postJson, apiDelete } from '../../lib/api.js'
import { nameInitial } from '../../lib/text.js'
import { relabelBranch } from '../../lib/asset.js'
import ConfirmDialog from '../ConfirmDialog.jsx'
import SearchableSelect from '../SearchableSelect.jsx'
import { relationLabel, fmtPercent, scoreTone, TONE_CLASS } from '../../lib/evaluation.js'

// What a รอบประเมิน actually contains once it has been dispatched: every subject, the
// evaluators asked about them, and who has answered.
//
// This screen exists because opening an already-sent round used to drop straight into step 1
// of the assignment wizard with nothing ticked — which looks identical to a round that was
// never sent, and gives no way to see what went out. Assigning MORE people is now a
// deliberate action from here rather than the default landing.
//
// Everything shown comes from GET /evaluation/task, so the peer-anonymity rule applies:
// HR/Admin see evaluator names, a Manager sees peer rows as "เพื่อนร่วมงาน (ไม่ระบุชื่อ)".

export default function EvaluationCycleDetail({ cycle, branches, onBack, onAssign }) {
  const [tasks, setTasks] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const [confirmClose, setConfirmClose] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [confirmDrop, setConfirmDrop] = useState(null)
  const [status, setStatus] = useState(cycle.status)
  const [employees, setEmployees] = useState([])
  const [doctors, setDoctors] = useState([])

  const load = useCallback(async () => {
    setLoading(true)
    setErr('')
    try {
      const t = await apiGet('/api/hr/evaluation/task', { cycle_id: cycle.id })
      setTasks(Array.isArray(t) ? t : [])
    } catch (e) {
      setErr(e?.message || 'โหลดรายละเอียดรอบประเมินไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [cycle.id])

  useEffect(() => { load() }, [load])

  // Rosters for the per-row "+ เพิ่มผู้ประเมิน" pickers. Loaded once; both are cached.
  // Doctors come from the full active list rather than the branch shift-roster used when the
  // grid was first proposed — this control exists precisely to reach someone the proposal
  // missed, so narrowing it back down would defeat it.
  useEffect(() => {
    let cancelled = false
    Promise.all([
      cachedGet('/api/hr/employees', { per_page: 500, active: 'true' }),
      cachedGet('/api/doctors', { per_page: 500 }),
    ])
      .then(([e, d]) => {
        if (cancelled) return
        setEmployees(Array.isArray(e) ? e : (e?.items || []))
        setDoctors(Array.isArray(d) ? d : (d?.items || []))
      })
      .catch(() => { /* pickers simply stay empty */ })
    return () => { cancelled = true }
  }, [])

  // subject → relation → tasks
  const subjects = useMemo(() => {
    const m = new Map()
    for (const t of tasks) {
      const k = t.subject_employee_id
      if (!m.has(k)) {
        m.set(k, {
          id: k,
          name: t.subject_nickname || t.subject_name || `#${k}`,
          position: t.subject_position,
          rows: new Map(),
        })
      }
      const s = m.get(k)
      if (!s.rows.has(t.relation)) s.rows.set(t.relation, [])
      s.rows.get(t.relation).push(t)
    }
    return [...m.values()].map((s) => ({ ...s, rows: [...s.rows.entries()] }))
  }, [tasks])

  const done = tasks.filter((t) => t.status === 'submitted').length

  const branchNameOf = useCallback((id) => {
    const b = (branches || []).find((x) => Number(x.id) === Number(id))
    return b ? relabelBranch(b.name) : `สาขา #${id}`
  }, [branches])

  const branchLabel = cycle.branch_id ? branchNameOf(cycle.branch_id) : 'ทุกสาขา'

  // Add one evaluator to an existing (subject, relation) row.
  //
  // Reuses POST /cycle/{id}/dispatch with a single assignment rather than inventing a new
  // "add one" endpoint: it already snapshots the form, already skips anyone who is a duplicate,
  // and reusing the SAME rule_id the row's existing tasks carry means the newcomer gets exactly
  // the same questionnaire and weight as the people beside them. `open_cycle: false` so adding
  // to a draft round does not silently release it.
  async function addEvaluator(subjectId, ruleId, relation, kind, id) {
    setBusy(true)
    setErr('')
    try {
      await postJson(`/api/hr/evaluation/cycle/${cycle.id}/dispatch`, {
        open_cycle: false,
        assignments: [{
          subject_employee_id: subjectId,
          relation,
          rule_id: ruleId,
          evaluators: [{ kind, id: Number(id) }],
        }],
      })
      await load()
    } catch (e) {
      setErr(e?.message || 'เพิ่มผู้ประเมินไม่สำเร็จ')
    } finally {
      setBusy(false)
    }
  }

  // Withdraw one unanswered questionnaire. Reloads rather than patching state locally, so the
  // counts and the subject grouping stay in step with what the server now holds.
  async function dropTask(task) {
    setConfirmDrop(null)
    setBusy(true)
    setErr('')
    try {
      await apiDelete(`/api/hr/evaluation/task/${task.id}`)
      await load()
    } catch (e) {
      setErr(e?.message || 'ถอนแบบประเมินไม่สำเร็จ')
    } finally {
      setBusy(false)
    }
  }

  async function remove() {
    setConfirmDelete(false)
    setBusy(true)
    try {
      await apiDelete(`/api/hr/evaluation/cycle/${cycle.id}`)
      onBack()
    } catch (e) {
      setErr(e?.message || 'ลบรอบประเมินไม่สำเร็จ')
      setBusy(false)
    }
  }

  async function setCycleStatus(next) {
    setConfirmClose(false)
    setBusy(true)
    try {
      await patchJson(`/api/hr/evaluation/cycle/${cycle.id}`, { status: next })
      setStatus(next)
    } catch (e) {
      setErr(e?.message || 'เปลี่ยนสถานะไม่สำเร็จ')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-[13px] text-slate-500 hover:text-slate-900 mb-3"
      >
        <ArrowLeft size={14} strokeWidth={1.75} /> กลับไปรายการรอบประเมิน
      </button>

      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2.5">
            <h2 className="text-[18px] font-semibold text-slate-900 tracking-tight">{cycle.title}</h2>
            <span className={`inline-flex rounded-full px-2 py-0.5 text-[11.5px] font-medium ring-1 ${
              status === 'open' ? TONE_CLASS.emerald : status === 'closed' ? TONE_CLASS.slate : TONE_CLASS.amber
            }`}>
              {status === 'open' ? 'เปิดอยู่' : status === 'closed' ? 'ปิดแล้ว' : 'ฉบับร่าง'}
            </span>
          </div>
          <p className="text-[13px] text-slate-500 mt-0.5">
            {cycle.type} · {branchLabel}
            {cycle.period_start ? ` · ${cycle.period_start} ถึง ${cycle.period_end || '-'}` : ''}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onAssign}
            className="px-4 py-2.5 rounded-xl text-[14px] font-semibold text-white bg-brand-600 hover:bg-brand-700 shadow-sm inline-flex items-center gap-2"
          >
            <UserPlus size={15} strokeWidth={1.75} /> เพิ่มผู้ถูกประเมิน
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => (status === 'closed' ? setCycleStatus('open') : setConfirmClose(true))}
            className="px-3 py-2.5 rounded-xl text-[13.5px] font-medium text-slate-600 border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1.5 disabled:opacity-40"
          >
            {busy ? <Loader2 size={14} className="animate-spin" />
              : status === 'closed' ? <Unlock size={14} strokeWidth={1.75} /> : <Lock size={14} strokeWidth={1.75} />}
            {status === 'closed' ? 'เปิดรอบอีกครั้ง' : 'ปิดรอบ'}
          </button>

          {/* Deleting is only offered while nothing has come back. Once an answer exists the
              round holds something a colleague actually wrote, so the button explains itself
              and points at ปิดรอบ instead of silently doing nothing. Xano refuses it too. */}
          <button
            type="button"
            disabled={busy || done > 0}
            onClick={() => setConfirmDelete(true)}
            title={done > 0
              ? 'ลบไม่ได้ เพราะมีผู้ส่งแบบประเมินแล้ว — ใช้ “ปิดรอบ” แทน'
              : 'ลบรอบประเมินนี้'}
            className="px-3 py-2.5 rounded-xl text-[13.5px] font-medium text-rose-600 border border-rose-200 hover:bg-rose-50 inline-flex items-center gap-1.5 disabled:opacity-30 disabled:cursor-not-allowed disabled:text-slate-400 disabled:border-slate-200 disabled:hover:bg-transparent"
          >
            <Trash2 size={14} strokeWidth={1.75} /> ลบรอบ
          </button>
        </div>
      </div>

      {err && <div className="text-[13px] text-rose-500 mb-3">{err}</div>}

      {!loading && tasks.length > 0 && (
        <div className="flex items-center gap-3 text-[13px] text-slate-600 mb-4">
          <span>ผู้ถูกประเมิน <span className="font-semibold text-slate-900">{subjects.length}</span> คน</span>
          <span className="text-slate-300">·</span>
          <span>ส่งแล้ว <span className="font-semibold text-slate-900">{tasks.length}</span> ชุด</span>
          <span className="text-slate-300">·</span>
          <span>ตอบกลับ <span className="font-semibold text-slate-900">{done}</span> ชุด</span>
          {tasks.length - done > 0 && (
            <>
              <span className="text-slate-300">·</span>
              <span className="text-amber-600">ค้าง {tasks.length - done} ชุด</span>
            </>
          )}
        </div>
      )}

      {loading && (
        <div className="flex items-center gap-2 text-[13.5px] text-slate-400 py-10 justify-center">
          <Loader2 size={15} className="animate-spin" /> กำลังโหลด…
        </div>
      )}

      {!loading && !tasks.length && (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-12 text-center">
          <p className="text-[14px] text-slate-500">ยังไม่ได้ส่งแบบประเมินในรอบนี้</p>
          <p className="text-[13px] text-slate-400 mt-1">กด “เพิ่มผู้ถูกประเมิน” เพื่อเลือกพนักงานและผู้ประเมิน แล้วส่ง</p>
        </div>
      )}

      <div className="space-y-3">
        {subjects.map((s) => {
          const mine = tasks.filter((t) => t.subject_employee_id === s.id)
          const sDone = mine.filter((t) => t.status === 'submitted').length
          return (
            // NOT overflow-hidden: each row holds a picker that opens an absolutely
            // positioned list, and clipping it to the card is what cut the dropdown down to a
            // single visible option. The header carries its own rounding instead.
            <div key={s.id} className="rounded-2xl border border-slate-200 bg-white">
              <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/60 rounded-t-2xl flex items-center gap-2.5">
                <div className="w-7 h-7 rounded-lg bg-white text-slate-500 flex items-center justify-center flex-none ring-1 ring-slate-200">
                  <span className="text-[11px] font-semibold">{nameInitial(s.name)}</span>
                </div>
                <span className="text-[13.5px] font-semibold text-slate-800">{s.name}</span>
                <span className="text-[12px] text-slate-400">{s.position}</span>
                <span className={`ml-auto inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${
                  sDone === mine.length ? TONE_CLASS.emerald : TONE_CLASS.amber
                }`}>
                  {sDone}/{mine.length} ชุด
                </span>
              </div>

              {s.rows.map(([relation, items]) => {
                const isDoctorRow = (items[0] || {}).evaluator_kind === 'doctor'
                const ruleId = (items[0] || {}).rule_id
                const options = isDoctorRow
                  ? doctors
                    .filter((d) => d.active !== false)
                    .map((d) => ({
                      value: String(d.id),
                      label: d.code || d.nickname || d.name || `#${d.id}`,
                      hint: 'ทันตแพทย์',
                    }))
                  : employees
                    .filter((e) => e.active !== false && e.id !== s.id)
                    .map((e) => ({
                      value: String(e.id),
                      label: e.code || e.nickname || e.name || `#${e.id}`,
                      hint: `${e.position || '—'} · ${branchNameOf(e.branch_id)}`,
                    }))
                return (
                <div key={relation} className="px-5 py-3 border-b border-slate-50 last:border-b-0">
                  <div className="text-[12.5px] font-medium text-slate-600 mb-1.5">
                    {relationLabel(relation)}
                    <span className="ml-2 text-[11.5px] font-normal text-slate-400">
                      {items.filter((t) => t.status === 'submitted').length}/{items.length}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 items-center">
                    {items.map((t) => {
                      const submitted = t.status === 'submitted'
                      // The server says whether this row was anonymised; never infer it from
                      // the label text.
                      const anon = t.anonymous === true
                      return (
                        <span
                          key={t.id}
                          title={submitted
                            ? `ส่งเมื่อ ${new Date(t.submitted_at).toLocaleString('th-TH')}`
                            : 'ยังไม่ได้ส่ง — กด × เพื่อถอนแบบประเมินนี้'}
                          className={`inline-flex items-center gap-1 rounded-full pl-2.5 pr-1.5 py-1 text-[12.5px] font-medium ring-1 ${
                            submitted ? TONE_CLASS[scoreTone(t.score_percent)] : TONE_CLASS.slate
                          }`}
                        >
                          {anon ? <ShieldCheck size={11} strokeWidth={2} />
                            : submitted ? <CheckCircle2 size={11} strokeWidth={2} />
                              : <Clock size={11} strokeWidth={2} />}
                          {t.evaluator || '—'}
                          {submitted && <span className="tabular-nums">· {fmtPercent(t.score_percent, 0)}</span>}
                          {/* Only an UNANSWERED questionnaire can be withdrawn. Once it has
                              been submitted the row holds real feedback that may already sit
                              inside a published score, so there is no × to press — the same
                              rule the endpoint enforces. */}
                          {!submitted ? (
                            <button
                              type="button"
                              onClick={() => setConfirmDrop(t)}
                              title="ถอนแบบประเมินนี้"
                              className="ml-0.5 p-0.5 rounded-full text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                            >
                              <X size={11} strokeWidth={2.5} />
                            </button>
                          ) : <span className="w-[14px]" />}
                        </span>
                      )
                    })}

                    {/* Add someone to THIS row. Single-select rather than multi: each pick is
                        an immediate write, so there is no half-applied state to lose, and the
                        chip appearing beside the picker is the confirmation. */}
                    <div className="w-56">
                      <SearchableSelect
                        options={options}
                        value=""
                        disabled={busy || !ruleId}
                        onChange={(v) => v && addEvaluator(s.id, ruleId, relation, isDoctorRow ? 'doctor' : 'employee', v)}
                        placeholder="+ เพิ่มผู้ประเมิน"
                        searchPlaceholder={isDoctorRow ? 'พิมพ์ชื่อหมอ…' : 'พิมพ์ชื่อ / ตำแหน่ง / สาขา…'}
                        emptyText="ไม่พบรายชื่อ"
                        buttonClassName="!py-1 !text-[12.5px]"
                      />
                    </div>
                  </div>
                </div>
                )
              })}
            </div>
          )
        })}
      </div>

      {confirmClose && (
        <ConfirmDialog
          title="ปิดรอบประเมิน?"
          message="ผู้ประเมินที่ยังไม่ได้ส่งจะส่งไม่ได้อีก และคะแนนจะคิดจากเท่าที่ได้รับมาแล้ว · เปิดใหม่ได้ภายหลัง"
          confirmLabel="ปิดรอบ"
          tone="danger"
          onConfirm={() => setCycleStatus('closed')}
          onCancel={() => setConfirmClose(false)}
        />
      )}

      {confirmDrop && (
        <ConfirmDialog
          title="ถอนแบบประเมินนี้?"
          message={`${confirmDrop.evaluator || 'ผู้ประเมินคนนี้'} จะไม่เห็นแบบประเมินของ ${confirmDrop.subject_nickname || confirmDrop.subject_name} ในรายการอีก · ยังไม่ได้ส่งคำตอบ จึงไม่มีคะแนนสูญหาย`}
          confirmLabel="ถอน"
          tone="danger"
          onConfirm={() => dropTask(confirmDrop)}
          onCancel={() => setConfirmDrop(null)}
        />
      )}

      {confirmDelete && (
        <ConfirmDialog
          title="ลบรอบประเมินนี้?"
          message={`จะลบแบบประเมิน ${tasks.length} ชุดที่ส่งออกไปแล้วออกจากรายการของผู้ประเมินทั้งหมด · ทำได้เพราะยังไม่มีใครส่งคำตอบกลับมา`}
          confirmLabel="ลบรอบ"
          tone="danger"
          onConfirm={remove}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </div>
  )
}
